import React from 'react'
import styled from '@emotion/styled'
import { colors, mediaQueriesByDevice } from 'parameters/index'
import { SummaryConfig } from './ResultsSection'
import { isDebtCalcError } from '../utils'
import { createPendingComponent, PendingFC, ResolvedConfig } from 'components/patterns/suspense/componentWrappers'
import { TextLineSkeleton } from 'components/patterns/Skeleton'
import { DebtConsolidationCalcResult } from 'generated/api'
import { formatCurrencyLocale } from 'lib'

const SummaryItemView: PendingFC<ResolvedConfig<SummaryConfig, DebtConsolidationCalcResult>> = props => {
	let top, mainValue, bottom

	if (props.pending) {
		top = <TextLineSkeleton width={150} />
		mainValue = <TextLineSkeleton width={180} />
		bottom = <TextLineSkeleton width={150} />
	} else {
		const {
			data,
			config: { topText, savingsErrorString, valueAddition, calculateProposedValue, calculateCurrentValue }
		} = props
		const error = isDebtCalcError(data.monthlyPaymentSummary?.bestValue)
		let value = ''
		let savings = ''
		if (error) {
			value = '$—'
			savings = savingsErrorString
		} else {
			value = `${formatCurrencyLocale(calculateProposedValue(data))}`
			savings = `${formatCurrencyLocale(calculateCurrentValue(data))}${valueAddition}`
		}
		top = <>{topText}</>
		mainValue = (
			<>
				<Value>{value}</Value>
				<Addition>{valueAddition}</Addition>
			</>
		)
		bottom = <>{`You Save ${savings}`}</>
	}

	return (
		<Item>
			<TopText>{top}</TopText>
			<MainValue>{mainValue}</MainValue>
			<BottomText>{bottom}</BottomText>
		</Item>
	)
}

export const SummaryItem = createPendingComponent(SummaryItemView)

const Item = styled.div`
	display: flex;
	flex-direction: column;
	width: 48%;
	margin-bottom: 32px;
	${mediaQueriesByDevice.mobile} {
		width: 45%;
		margin-bottom: 16px;
	}
`

const MainValue = styled.div`
	font-weight: bold;
	height: 43px;
	margin-bottom: 4px;
	color: ${colors.black};
	> span {
		height: 90%;
	}
`

const Value = styled.span`
	font-size: 32px;
`

const Addition = styled.span`
	font-size: 24px;
`

const TopText = styled.span`
	font-size: 14px;
	font-weight: normal;
	margin-bottom: 8px;
	color: ${colors.secondaryDarkGrey1};
`

const BottomText = styled.span`
	font-size: 16px;
	font-weight: normal;
	color: ${colors.primaryGreen1};
`
